import { FunctionComponent, memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../../../context';
import { Story, storyApi, StoryStatus } from '../../../../../../services';
import { useAppDispatch } from '../../../../../hooks';
import { AutoComplete } from '../../../../AutoComplete';

import classes from './StoryNodeSelect.module.scss';

interface StoryNodeSelectProps {
  story: Story | null;
  setStory: (story: Story) => void;
  onFocus: () => void;
}

export const StoryNodeSelect: FunctionComponent<StoryNodeSelectProps> = memo(
  ({ story, setStory, onFocus }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { channelId } = useContext(UserContext).userInfo.userData;

    const itemTemplate = useCallback(({ title, subtitle, image }: Story) => {
      return (
        <div className={classes['story']}>
          <div className={classes['story__fields']}>
            <div className={classes['story__fields-title']}>{title}</div>
            {subtitle && <div className={classes['story__fields-subTitle']}>{subtitle}</div>}
          </div>
          {image?.url && (
            <div
              className={classes['story__teaser']}
              style={{ backgroundImage: `url(${image.url})` }}
            ></div>
          )}
        </div>
      );
    }, []);

    const fetchQuery = useCallback(() => {
      return dispatch(
        storyApi.endpoints.getStoriesByChannelId.initiate({
          channelId,
          filter: { onlyPostIn: false, status: [StoryStatus.PUBLISHED] },
        })
      );
    }, [channelId, dispatch]);

    const onChange = useCallback(
      (stories: Story[]) => setStory(stories.slice(-1).pop() as Story),
      [setStory]
    );

    return (
      <AutoComplete
        propToIdentify={'id'}
        propToDisplay={'title'}
        fetchQuery={fetchQuery}
        itemTemplate={itemTemplate}
        value={[story].filter(Boolean)}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={t('richTextEditor.search-story-placeholder')}
        isStatic
      />
    );
  }
);
